/** @format */

import { useNavigate } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	CssBaseline,
	Grid,
	TextField,
	Typography,
	Modal,
	Backdrop,
	Fade,
	alpha,
} from "@mui/material";
import { Form, Formik } from "formik";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React, { useState } from "react";
import { colors } from "../../utility/types/constant";
import {
	EmptyVerifyAndConfirmAccountPayload,
	IVerifyAndConfirmAccountPayload,
} from "./types";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import {
	confirmAndVerifyAccount,
	resendVerificationCodeAction,
} from "../../redux/actions/userActions";
import { unwrapResult } from "@reduxjs/toolkit";
import { verifyAndConfirmAccountSchema } from "../../utility/validation/verifyAccountSchemaValidation";
import { setAuthenticationState } from "../../redux/reducers/userReducer";

const BENEFITS = [
	"Create or join savings circles with people you trust",
	"Automated contributions and scheduled payouts",
	"Bank-level security powered by Stripe",
	"Available in 140+ countries worldwide",
];

const VerifyAndConfirmAccount = () => {
	const dispatch = useDispatch<AppDispatch>();

	const navigate = useNavigate();

	const [initialValues, setInitialValues] =
		useState<IVerifyAndConfirmAccountPayload>(
			EmptyVerifyAndConfirmAccountPayload,
		);

	const [success, setSuccess] = useState(false);

	const [errorMessage, setErrorMessage] = useState("");

	const [loading, setLoading] = useState(false);

	const [resendLoading, setResendLoading] = useState(false);

	const [resendSuccess, setResendSuccess] = useState(false);

	const [resendError, setResendError] = useState("");

	const handleSubmit = async (values: IVerifyAndConfirmAccountPayload) => {
		setLoading(true);
		setErrorMessage("");
		setSuccess(false);
		try {
			const result = await dispatch(confirmAndVerifyAccount(values));

			const data = unwrapResult(result);

			if (data.message) {
				dispatch(setAuthenticationState(true));
				setSuccess(true);
				setTimeout(() => {
					navigate("/circle/sign-in");
				}, 10000);
			} else {
				setErrorMessage(
					"Verification Failed or Token Invalid. Please try again",
				);
			}

			setInitialValues(values);
			setSuccess(true);
		} catch (err: any) {
			setErrorMessage(
				err.message ||
				"An Error Occurred During Verification. Make sure you have correct access code",
			);
			setSuccess(false);
		} finally {
			setLoading(false);
		}
	};

	const handleResendCode = async (email: string) => {
		if (!email) {
			setResendError("Please enter your email address first.");
			setTimeout(() => {
				setResendError("");
			}, 5000);
			return;
		}

		setResendLoading(true);
		setResendError("");
		setResendSuccess(false);

		try {
			const result = await dispatch(resendVerificationCodeAction(email));
			const data = unwrapResult(result);

			if (data.message) {
				setResendSuccess(true);
				setTimeout(() => {
					setResendSuccess(false);
				}, 5000);
			}
		} catch (err: any) {
			setResendError(
				err.message || "Failed to resend verification code. Please try again.",
			);
			setTimeout(() => {
				setResendError("");
			}, 5000);
		} finally {
			setResendLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: colors.primary,
				display: "flex",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<CssBaseline />

			{/* Success Modal */}
			<Modal
				open={success}
				closeAfterTransition
				slots={{ backdrop: Backdrop }}
				slotProps={{
					backdrop: {
						timeout: 500,
						sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
					},
				}}
			>
				<Fade in={success}>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
							borderRadius: 4,
							boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
							p: 5,
							textAlign: "center",
							minWidth: 320,
							maxWidth: 420,
							outline: "none",
						}}
					>
						<Box
							sx={{
								width: 80,
								height: 80,
								borderRadius: "50%",
								background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mx: "auto",
								mb: 3,
								boxShadow: "0 8px 24px rgba(76, 175, 80, 0.4)",
							}}
						>
							<CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#fff" }} />
						</Box>
						<Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: "#2e7d32", mb: 1 }}>
							Account Verified!
						</Typography>
						<Typography variant="body1" sx={{ color: "#555", mb: 3, lineHeight: 1.6 }}>
							Your account is now verified and confirmed.
						</Typography>
						<Typography variant="body2" sx={{ color: "#888", fontStyle: "italic" }}>
							Redirecting to Sign In...
						</Typography>
						<CircularProgress size={24} sx={{ color: "#4CAF50", mt: 2 }} />
					</Box>
				</Fade>
			</Modal>

			{/* Background blobs */}
			<Box
				sx={{
					position: "absolute",
					top: -200,
					right: -200,
					width: 600,
					height: 600,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.07)} 0%, transparent 70%)`,
					pointerEvents: "none",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					bottom: -150,
					left: -150,
					width: 500,
					height: 500,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.05)} 0%, transparent 70%)`,
					pointerEvents: "none",
				}}
			/>

			{/* Main layout */}
			<Box
				sx={{
					display: "flex",
					width: "100%",
					maxWidth: 1100,
					mx: "auto",
					alignItems: "center",
					px: { xs: 2, sm: 3, md: 5 },
					py: { xs: 3, md: 0 },
					minHeight: "100vh",
				}}
			>
				{/* Left branding panel (desktop only) */}
				<Box
					sx={{
						display: { xs: "none", lg: "flex" },
						flexDirection: "column",
						justifyContent: "center",
						flex: "0 0 46%",
						pr: 8,
						py: 6,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								overflow: "hidden",
								boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Box
								component="img"
								src="/images/how-it-works-images/primary-logo-1-1.png"
								alt="FundsyFi"
								sx={{ width: 42, height: 42, objectFit: "contain", display: "block" }}
							/>
						</Box>
						<Typography sx={{ color: "#fff", fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "1.5rem", letterSpacing: 0.3 }}>
							FundsyFi
						</Typography>
					</Box>
					<Typography
						sx={{
							color: "#fff",
							fontFamily: "'Montserrat', sans-serif",
							fontWeight: 800,
							fontSize: { lg: "2.2rem", xl: "2.6rem" },
							lineHeight: 1.2,
							mb: 2,
						}}
					>
						Almost{" "}
						<Box component="span" sx={{ color: colors.mainSecondary }}>
							there.
						</Box>
					</Typography>
					<Typography
						sx={{
							color: alpha("#fff", 0.55),
							mb: 4,
							lineHeight: 1.7,
							fontSize: "0.95rem",
							maxWidth: 380,
						}}
					>
						Check your inbox for the verification code we sent you, then enter it below to activate your account.
					</Typography>
					{BENEFITS.map((benefit, i) => (
						<Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
							<CheckCircleIcon sx={{ color: colors.mainSecondary, fontSize: "1.1rem", mt: 0.3, flexShrink: 0 }} />
							<Typography sx={{ color: alpha("#fff", 0.75), fontSize: "0.88rem", lineHeight: 1.55 }}>
								{benefit}
							</Typography>
						</Box>
					))}
				</Box>

				{/* Right form card */}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						py: { xs: 0, lg: 4 },
					}}
				>
					<Box
						sx={{
							width: "100%",
							maxWidth: 460,
							bgcolor: "#fff",
							borderRadius: { xs: 3, sm: 4 },
							p: { xs: 3, sm: 4 },
							boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
							position: "relative",
							zIndex: 1,
						}}
					>
						{/* Mobile logo */}
						<Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", gap: 1, mb: 2.5 }}>
							<Box
								sx={{
									width: 32,
									height: 32,
									borderRadius: 1.5,
									overflow: "hidden",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Box
									component="img"
									src="/images/how-it-works-images/primary-logo-1-1.png"
									alt="FundsyFi"
									sx={{ width: 32, height: 32, objectFit: "contain", display: "block" }}
								/>
							</Box>
							<Typography sx={{ color: colors.primary, fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "1.1rem" }}>
								FundsyFi
							</Typography>
						</Box>

						<Typography
							sx={{
								fontFamily: "'Montserrat', sans-serif",
								fontWeight: 800,
								color: colors.primary,
								fontSize: "1.4rem",
								mb: 0.4,
							}}
						>
							Verify & Confirm
						</Typography>
						<Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.5 }}>
							Enter the verification code sent to your email to activate your account.
						</Typography>

						{errorMessage && (
							<Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
								Error — <strong>{errorMessage}</strong>
							</Alert>
						)}
						{resendSuccess && (
							<Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
								<strong>Verification code has been resent to your email.</strong>
								<br />
								<span style={{ fontSize: "0.875rem" }}>
									Please allow 1–2 minutes for delivery. Don't forget to check your spam folder.
								</span>
							</Alert>
						)}
						{resendError && (
							<Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
								Error — <strong>{resendError}</strong>
							</Alert>
						)}

						<Formik
							initialValues={initialValues}
							onSubmit={handleSubmit}
							validationSchema={verifyAndConfirmAccountSchema}
						>
							{({ errors, touched, handleChange, values }) => (
								<Form>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
										<TextField
											size="small"
											error={!!(errors?.email && touched?.email)}
											helperText={errors.email && touched?.email && errors.email}
											required
											fullWidth
											id="email"
											label="Email Address"
											name="email"
											autoComplete="email"
											autoFocus
											value={values.email}
											onChange={handleChange}
										/>
										<TextField
											size="small"
											error={!!(errors?.code && touched?.code)}
											helperText={errors.code && touched?.code && errors.code}
											required
											fullWidth
											name="code"
											label="Access Code"
											type="text"
											id="code"
											autoComplete="one-time-code"
											value={values.code}
											onChange={handleChange}
										/>

										<Box
											sx={{
												p: 1.5,
												bgcolor: alpha(colors.primary, 0.04),
												borderRadius: 1.5,
												border: `1px solid ${alpha(colors.primary, 0.1)}`,
											}}
										>
											<Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
												A verification code was sent to your email. Haven't received it?{" "}
												<Box component="span" sx={{ color: colors.primary, fontWeight: 600 }}>
													Please wait at least 1 minute
												</Box>{" "}
												before requesting a new code below.
											</Typography>
										</Box>

										{loading ? (
											<Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
												<CircularProgress size={24} />
											</Box>
										) : (
											<Button
												type="submit"
												fullWidth
												variant="contained"
												sx={{
													bgcolor: colors.mainSecondary,
													color: colors.primary,
													fontWeight: 800,
													py: 1.2,
													borderRadius: 2,
													textTransform: "none",
													fontSize: "0.95rem",
													letterSpacing: 0.3,
													boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
													"&:hover": {
														bgcolor: colors.mainSecondary,
														boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.5)}`,
													},
												}}
											>
												Verify Account
											</Button>
										)}

										<Grid container spacing={1.5}>
											<Grid item xs={12} sm={6}>
												<Button
													fullWidth
													variant="outlined"
													onClick={(e) => {
														e.preventDefault();
														handleResendCode(values.email);
													}}
													disabled={resendLoading}
													sx={{
														borderColor: colors.primary,
														color: colors.primary,
														textTransform: "none",
														borderRadius: 2,
														"&:hover": { borderColor: colors.primary },
													}}
												>
													{resendLoading ? "Resending..." : "Resend Code"}
												</Button>
											</Grid>
											<Grid item xs={12} sm={6}>
												<Button
													fullWidth
													variant="outlined"
													onClick={() => navigate("/circle/sign-up")}
													sx={{
														borderColor: colors.primary,
														color: colors.primary,
														textTransform: "none",
														borderRadius: 2,
														"&:hover": { borderColor: colors.primary },
													}}
												>
													Sign Up
												</Button>
											</Grid>
										</Grid>
									</Box>
								</Form>
							)}
						</Formik>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default VerifyAndConfirmAccount;
